/**
 * Responsive existing-inventory decision row (velocity + scenario plan
 * Task 4, 2026-08-26/31 —
 * docs/plans/2026-08-26-price-calibration-existing-inventory-design.md).
 * Replaces PriceCalibrationPage.tsx's old dense `CalibrationRow` <tr>: one
 * component renders BOTH a reduced desktop decision grid and a stacked
 * tablet/mobile card from the same derived data, so the two layouts can
 * never drift. All math is delegated to lib/price-calibration.ts's
 * PriceCalibrationRow adapters (which delegate to lib/profit-scenario.ts's
 * page-neutral kernel) — this file owns presentation/copy only.
 *
 * The clarity pass (2026-09-04) reduces that six-column presentation to four
 * labeled jobs: Product; Inventory & demand; Price scenario; Suggested
 * action. It changes presentation only: the existing scenario map, row math,
 * portfolio summary, and expanded MathCard remain the sources of truth.
 *
 * Competitor stock and clearance (2026-10-05 —
 * docs/plans/2026-10-05-competitor-stock-clearance-design.md) add three
 * things, all driven by `row.competition` / `row.clearance`: a competitor
 * line under the market line, a Clearance block that replaces the verdict
 * advice in the Suggested action column, and a Competing sellers table under
 * the MathCard. Rows without a snapshot render exactly as before.
 *
 * Seller Central block (2026-10-05): every row with a price also shows the
 * three numbers Seller Central asks for (price, min, max), ready to type in,
 * under the advice in the Suggested action column. The numbers come from
 * lib/price-calibration.ts's sellerCentralPrices; Sourcito never writes them
 * to Amazon.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Minus, Plus } from 'lucide-react';
import { Badge, Button, cn } from '../../design';
import {
  clearanceHeadline,
  clearanceMoneyLine,
  competitionSummaryLine,
  costLine,
  feesSourceNote,
  feesSourceTag,
  marginPercent,
  money,
  parseSimulatedPriceInput,
  priceCalibrationRowToMathCardInput,
  priceFreshnessLabel,
  proposedPriceForRow,
  rowScenarioResult,
  salesRankSegments,
  type SalesRankSegment,
  scenarioDeltaLabel,
  sellerCentralBasisLine,
  sellerCentralCopyLine,
  sellerCentralPrices,
  type SellerCentralPrices,
  sellThroughDurationLabel,
  selectedUnitsForRow,
  signedMoney,
  SIMULATOR_STEP_CENTS,
  stepSimulatedPriceCents,
  stockReadingLabel,
  verdictBadge,
} from '../../lib/price-calibration';
import { authorizationBadge, buildMathCard } from '../../lib/order-planning';
import { computeDiscountOffset, type InventoryScope } from '../../lib/profit-scenario';
import { lowestNoLossPriceCents, REASON_LABELS, type ReasonTag } from '../../lib/price-strategies';
import { ProductLink } from '../../components/ProductLink';
import { ProductThumb } from '../../components/ProductThumb';
import { MathCard } from './MathCard';
import type {
  CompetitorOffer,
  PriceCalibrationRow as PriceCalibrationRowData,
} from '@sourcito/schemas';

/** Row's out-of-stock-in-scope copy — the design doc's rule that a zero-
 *  selected-units row contributes nothing to totals and is called out
 *  rather than showing a misleading "0 days"/no-context number. */
export const ZERO_STOCK_ROW_LABEL = 'Out of stock in this scope — excluded from totals';

/** Per-arrow tint for one salesRankSegments delta — positive/negative get
 *  the row's existing text-positive/text-negative idiom (see the
 *  buyboxIsYours "yours" span below); neutral/null (unchanged, or no
 *  comparison yet) render untinted so the ▼/— text doesn't borrow a color
 *  it didn't earn. */
function rankSegmentClass(tone: SalesRankSegment['tone']): string | undefined {
  if (tone === 'positive') return 'text-positive';
  if (tone === 'negative') return 'text-negative';
  return undefined;
}

/** Discount give-up copy — pure formatting over profit-scenario.ts's
 *  computeDiscountOffset result; no math happens here. Null (renders
 *  nothing) when there's no active discount to explain (a raise, or no
 *  scenario at all — the caller already gates on that). */
function discountOffsetLine(offset: ReturnType<typeof computeDiscountOffset>): string | null {
  if (offset.giveUpPerUnitCents == null || offset.giveUpPerUnitCents <= 0) return null;
  if (!offset.recoverable) {
    return `Gives up ${money(offset.giveUpPerUnitCents)}/unit — this discount can't be recovered by selling more units under the current cost and fee assumptions.`;
  }
  if (offset.unitsNeeded == null) {
    return `Gives up ${money(offset.giveUpPerUnitCents)}/unit — current sales velocity is unknown, so there's no baseline to size the recovery against.`;
  }
  return `Gives up ${money(offset.giveUpPerUnitCents)}/unit — sell ${offset.unitsNeeded.toLocaleString('en-US')} more unit${offset.unitsNeeded === 1 ? '' : 's'} at this price to match your current 30-day profit.`;
}

/** One market sentence replaces the old repeated price, delta, and compact
 * competition-summary lines. */
function marketContextLine(row: PriceCalibrationRowData): string {
  if (row.buyboxPriceCents === null) {
    return 'No featured offer (Buy Box) on this listing right now.';
  }

  const nearby =
    row.offersWithin5Pct === null
      ? ''
      : ` · ${row.offersWithin5Pct.toLocaleString('en-US')} offer${
          row.offersWithin5Pct === 1 ? '' : 's'
        } nearby`;
  if (row.buyboxIsYours) return `You hold the ${money(row.buyboxPriceCents)} Buy Box${nearby}`;
  if (row.yourPriceCents === null) return `Buy Box ${money(row.buyboxPriceCents)}${nearby}`;

  const delta = row.buyboxPriceCents - row.yourPriceCents;
  if (delta === 0) return `Your price matches the ${money(row.buyboxPriceCents)} Buy Box${nearby}`;
  return `Buy Box is ${money(Math.abs(delta))} ${delta > 0 ? 'above' : 'below'} yours${nearby}`;
}

interface SuggestedAction {
  title: string;
  reason: string;
}

const STRATEGY_REASON_SENTENCES: Record<ReasonTag, string> = {
  'to-target': 'The active strategy raised this SKU to its target-margin price.',
  'under-buybox': 'The active strategy priced below the Buy Box without crossing break-even.',
  'under-competitor':
    'The active strategy priced below the nearest competitor without crossing break-even.',
  'at-min-price': 'The active strategy moved this SKU to its configured minimum price.',
  'break-even': 'The active strategy moved this SKU to break-even.',
  'exit-at-market': 'The active strategy matched the market to exit this underwater inventory.',
  'raised-selling-well': 'The active strategy raised a fast-selling SKU toward target margin.',
  'cut-slow-mover': 'The active strategy lowered a slow-moving SKU while protecting break-even.',
  held: 'The active strategy found no useful price change for this SKU.',
  clearance: 'The active strategy used the clearance price from competitor stock.',
};

function suggestedAction(
  row: PriceCalibrationRowData,
  proposedPriceCents: number | null,
  isSimulating: boolean,
  reasonTag: ReasonTag | null,
  breakEvenCents: number | null
): SuggestedAction {
  if (isSimulating && proposedPriceCents !== null) {
    const direction =
      proposedPriceCents > (row.yourPriceCents ?? proposedPriceCents) ? 'Raise' : 'Lower';
    return {
      title: reasonTag
        ? `${direction} to ${money(proposedPriceCents)}`
        : `Your test: ${direction.toLowerCase()} to ${money(proposedPriceCents)}`,
      reason: reasonTag
        ? STRATEGY_REASON_SENTENCES[reasonTag]
        : 'Manual scenario; no strategy recommendation is attached.',
    };
  }
  if (reasonTag === 'held' && row.yourPriceCents !== null) {
    return {
      title: `Keep ${money(row.yourPriceCents)}`,
      reason: STRATEGY_REASON_SENTENCES.held,
    };
  }
  if (row.verdict === 'optimal' && row.yourPriceCents !== null) {
    return {
      title: `Keep ${money(row.yourPriceCents)} for now`,
      reason: 'Current price meets your target margin.',
    };
  }
  if (row.verdict === 'underwater') {
    if (breakEvenCents !== null) {
      return {
        title: `Raise to ${money(breakEvenCents)} to break even`,
        reason: 'Covers landed cost and fees with no loss.',
      };
    }
    return {
      title: 'Needs a pricing decision',
      reason:
        row.currentMarginCents !== null
          ? `Current price loses ${money(Math.abs(row.currentMarginCents))} per unit.`
          : 'Current price loses money.',
    };
  }
  if (row.verdict === 'below-floor') {
    if (row.minPriceCents !== null) {
      return {
        title: `Raise to ${money(row.minPriceCents)} minimum`,
        reason:
          breakEvenCents !== null
            ? `Reaches your minimum margin. Break-even price: ${money(breakEvenCents)}.`
            : 'Reaches your configured minimum margin.',
      };
    }
    return {
      title: 'Review price or exit',
      reason: 'Current price is below your minimum margin.',
    };
  }
  if (row.verdict === 'below-target') {
    return {
      title: 'Review margin',
      reason: 'Current price is profitable but below your target margin.',
    };
  }
  if (row.verdict === 'no-price') {
    return { title: 'Check the listing', reason: 'There is no active Amazon offer to price.' };
  }
  return { title: 'Add a cost basis', reason: 'Profit cannot be evaluated without a cost.' };
}

function rowWatchouts(
  row: PriceCalibrationRowData,
  selectedUnits: number,
  recentUnitsPerDay: number | null,
  reasonTag: ReasonTag | null
): string[] {
  const concerns: string[] = [];
  const costEstimated =
    row.costBasisGrade === 'estimate' ||
    (row.costBasisGrade === null && row.costBasis === 'supplier_x_pack');
  const feesEstimated = row.feesSource === 'estimated';
  if (costEstimated && feesEstimated) concerns.push('Cost and fees are estimated.');
  else if (costEstimated) concerns.push('Cost basis is estimated.');
  else if (feesEstimated) concerns.push('Amazon fees are estimated.');

  if (!row.velocityKnown || recentUnitsPerDay === null) {
    concerns.push('Sell-through time is unknown.');
  } else if (
    reasonTag !== 'cut-slow-mover' &&
    (recentUnitsPerDay === 0 || selectedUnits / recentUnitsPerDay > 90)
  ) {
    concerns.push('More than 90 days of inventory remain at the recent sales rate.');
  }

  const rank = row.salesRank;
  if (rank) {
    const previous = rank.rank30dAgo ?? rank.rank7dAgo;
    if (previous !== null && rank.rank > previous) {
      const period = rank.rank30dAgo !== null ? '30 days' : '7 days';
      concerns.push(
        `Sales rank worsened by ${(rank.rank - previous).toLocaleString('en-US')} in ${period}.`
      );
    }
  }

  return concerns.slice(0, 2);
}

export interface PriceCalibrationRowProps {
  row: PriceCalibrationRowData;
  scope: InventoryScope;
  thresholds: { minFrac: number; targetFrac: number } | null;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** This row's active tried price, or null when priced at the live price. */
  simulatedCents: number | null;
  onSimulate: (cents: number | null) => void;
  /** This row's Strategy-menu reason tag (pricing-strategies design, Task
   *  C), or null when no strategy has touched this row, or a hand edit
   *  (`onSimulate`) has since cleared it — see the module doc on
   *  CalibrationList's reasonTags map. Rendered as a small pill beside the
   *  stepper only while a tried price is actually active. */
  reasonTag: ReasonTag | null;
}

export function PriceCalibrationRow({
  row,
  scope,
  thresholds,
  expanded,
  onToggleExpanded,
  simulatedCents,
  onSimulate,
  reasonTag,
}: PriceCalibrationRowProps) {
  const label = row.productName ?? row.sellerSku;
  const badge = verdictBadge(row.verdict);
  const authBadge = authorizationBadge(row.authorization);
  const mathCardInput = priceCalibrationRowToMathCardInput(row, thresholds);

  const selectedUnits = selectedUnitsForRow(row, scope);
  const isOutOfStock = selectedUnits <= 0;
  const recentUnitsPerDay = row.velocityKnown ? row.recentUnitsPerDay : null;
  const durationLabel = sellThroughDurationLabel(selectedUnits, recentUnitsPerDay);

  const scenarios =
    simulatedCents !== null
      ? new Map([[row.sellerSku, simulatedCents]])
      : new Map<string, number>();
  const scenario = rowScenarioResult(row, scope, scenarios);
  const proposedPriceCents = proposedPriceForRow(row, scenarios);
  const isSimulating = simulatedCents !== null && simulatedCents !== row.yourPriceCents;
  const discountOffset = isSimulating
    ? computeDiscountOffset(scenario.current, scenario.proposed)
    : null;
  const discountLine = discountOffset ? discountOffsetLine(discountOffset) : null;

  const notSellableNow = Math.max(0, row.allOwnedUnits - row.sellableNowUnits);
  const inventoryHeading = `${row.sellableNowUnits.toLocaleString('en-US')} sellable${
    notSellableNow > 0 ? ` · ${notSellableNow.toLocaleString('en-US')} not sellable now` : ''
  }`;
  const rankSegments = salesRankSegments(row);
  const demandLine = isOutOfStock
    ? `${row.unitsSold30d.toLocaleString('en-US')} sold in 30d · ${ZERO_STOCK_ROW_LABEL}`
    : recentUnitsPerDay !== null && recentUnitsPerDay > 0
      ? `${row.unitsSold30d.toLocaleString('en-US')} sold in 30d · ${durationLabel} left at recent rate`
      : `${row.unitsSold30d.toLocaleString('en-US')} sold in 30d · sell-through time unknown`;

  const currentUnitProfitLabel =
    scenario.current.unitProfitCents !== null ? money(scenario.current.unitProfitCents) : '—';
  const currentRemainingProfitLabel =
    scenario.current.remainingInventoryProfitCents !== null
      ? money(scenario.current.remainingInventoryProfitCents)
      : '—';
  const proposedUnitProfitLabel =
    scenario.proposed.unitProfitCents !== null ? money(scenario.proposed.unitProfitCents) : '—';
  const proposedRemainingProfitLabel =
    scenario.proposed.remainingInventoryProfitCents !== null
      ? money(scenario.proposed.remainingInventoryProfitCents)
      : '—';
  const proposedMarginPct =
    proposedPriceCents !== null &&
    proposedPriceCents > 0 &&
    scenario.proposed.unitProfitCents !== null
      ? scenario.proposed.unitProfitCents / proposedPriceCents
      : null;
  const breakEvenCents = lowestNoLossPriceCents({
    costCents: row.landedUnitCostCents,
    referralPct: row.referralPct,
    fixedFeeCents: row.fixedFeeCents,
  });
  const marketLine = marketContextLine(row);
  const action = suggestedAction(row, proposedPriceCents, isSimulating, reasonTag, breakEvenCents);
  const watchouts = rowWatchouts(row, selectedUnits, recentUnitsPerDay, reasonTag);
  // The row's break-even is already lowestNoLossPriceCents, so the price rule
  // and the min rule read the same number.
  const sellerCentral = sellerCentralPrices(row, {
    proposedPriceCents,
    isSimulating,
    breakEvenCents,
    lowestNoLossCents: breakEvenCents,
  });
  const { competition, clearance } = row;
  const now = new Date();
  // Clearance price the "Use this price" button can apply: only when there is
  // one and it is not already the live price.
  const clearancePriceToTry =
    clearance && clearance.priceCents !== null && clearance.priceCents !== row.yourPriceCents
      ? clearance.priceCents
      : null;

  // ── Shared fields (rendered by both the desktop grid and the compact
  //    card — one derivation, two layouts, per the module doc above). ──

  const productField = (
    <div className="flex items-start gap-3 min-w-0">
      <ProductThumb imageUrl={row.imageUrl} alt={label} size="sm" />
      <div className="min-w-0">
        <p className="text-sm text-fg-primary line-clamp-2" title={label}>
          <ProductLink asin={row.asin}>{label}</ProductLink>
        </p>
        <p className="text-xs font-mono text-fg-tertiary truncate">
          {row.asin ?? '—'} · {row.sellerSku}
        </p>
      </div>
    </div>
  );

  const unitsField = (
    <div>
      <p className="text-sm font-medium tabular-nums">{inventoryHeading}</p>
      <p className="text-xs text-fg-secondary mt-1 tabular-nums">{demandLine}</p>
      {rankSegments && (
        // Rendered whenever this ASIN has an observed rank, including
        // out-of-stock rows — a rank is useful for a restock decision
        // (docs/plans/2026-09-03-sales-rank-on-calibration.md). Each
        // "vs N ago" segment gets its own tone so a 7d improvement and a
        // 30d regression can never share one color.
        <p className="text-xs text-fg-tertiary mt-1 tabular-nums">
          {rankSegments.head}
          {row.salesRank?.rank7dAgo != null && (
            <>
              {' · '}
              <span className={rankSegmentClass(rankSegments.vs7d.tone)}>
                {rankSegments.vs7d.text}
              </span>
            </>
          )}
          {row.salesRank?.rank30dAgo != null && (
            <>
              {' · '}
              <span className={rankSegmentClass(rankSegments.vs30d.tone)}>
                {rankSegments.vs30d.text}
              </span>
            </>
          )}
        </p>
      )}
    </div>
  );

  const priceScenarioField = (
    <div>
      {row.yourPriceCents === null ? (
        // Founder UAT bonus (2026-08-30): a stocked row with no current
        // price said nothing but bare dashes across every line below — say
        // plainly why instead. Rows with no price stay in the default list
        // (see visiblePriceCalibrationRows) precisely so this real pricing
        // gap stays visible.
        <p className="text-fg-secondary">No active Amazon offer, check the listing.</p>
      ) : (
        <>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 items-center">
            <p className="text-xs text-fg-tertiary">Current</p>
            <p className="text-sm font-medium tabular-nums">{money(row.yourPriceCents)}</p>
            <p className="text-xs text-fg-tertiary">Try price</p>
            <div className="flex items-center gap-1.5 min-w-0">
              <PriceStepper
                liveCents={row.yourPriceCents}
                simulatedCents={simulatedCents}
                onSimulate={onSimulate}
              />
              {simulatedCents !== null && reasonTag && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-surface-elev border border-border-subtle text-fg-tertiary whitespace-nowrap">
                  {REASON_LABELS[reasonTag]}
                </span>
              )}
            </div>
          </div>

          <div className={cn('mt-2 grid gap-3', isSimulating && 'grid-cols-2')}>
            <div>
              <p className="text-xs text-fg-tertiary">
                {isSimulating ? 'Current outcome' : 'No price change'}
              </p>
              <p className="text-sm tabular-nums mt-0.5">
                {currentUnitProfitLabel} per unit ({marginPercent(row.currentMarginPct)})
              </p>
              <p className="text-xs text-fg-secondary tabular-nums mt-0.5">
                Across {selectedUnits.toLocaleString('en-US')} units in scope:{' '}
                {currentRemainingProfitLabel}
              </p>
            </div>
            {isSimulating && (
              <div>
                <p className="text-xs text-fg-tertiary">Outcome at {money(proposedPriceCents)}</p>
                <p className="text-sm tabular-nums mt-0.5">
                  {proposedUnitProfitLabel} per unit ({marginPercent(proposedMarginPct)})
                </p>
                <p className="text-xs text-fg-secondary tabular-nums mt-0.5">
                  Across {selectedUnits.toLocaleString('en-US')} units in scope:{' '}
                  {proposedRemainingProfitLabel}
                </p>
              </div>
            )}
          </div>
          <p className="text-xs text-fg-secondary mt-2">{marketLine}</p>
          {competition && (
            <>
              <p className="text-xs text-fg-secondary mt-1">
                {competitionSummaryLine(competition, now)}
              </p>
              {competition.salesDataStale && (
                <p className="text-xs text-fg-tertiary mt-1">
                  Our sales data is out of date, so this ignores our recent sales.
                </p>
              )}
            </>
          )}
        </>
      )}
      {isSimulating && (
        <div className="mt-1.5 space-y-1 text-xs text-fg-secondary">
          <p className="tabular-nums text-accent-fg">
            {scenarioDeltaLabel(scenario.sellThroughProfitDeltaCents)}
          </p>
          {discountLine && <p className="text-warning">{discountLine}</p>}
          {isSimulating && proposedPriceCents !== null && (
            <button
              type="button"
              onClick={() => onSimulate(null)}
              className="text-accent-fg hover:underline"
            >
              simulating {money(proposedPriceCents)} — reset
            </button>
          )}
        </div>
      )}
    </div>
  );

  const watchoutsBlock =
    watchouts.length > 0 ? (
      <div className="mt-2 space-y-1" aria-label="Watchouts">
        <p className="text-xs font-medium text-fg-secondary">Watchouts</p>
        {watchouts.map((watchout) => (
          <p key={watchout} className="text-xs text-warning">
            {watchout}
          </p>
        ))}
      </div>
    ) : null;

  const sellerCentralBlock = sellerCentral ? (
    <SellerCentralBlock
      sku={row.sellerSku}
      prices={sellerCentral}
      currentPriceCents={row.yourPriceCents}
    />
  ) : null;

  const clearanceMoney = clearance ? clearanceMoneyLine(clearance, row.sellableNowUnits) : null;

  // A row with a clearance recommendation shows it INSTEAD of the verdict
  // advice: "Raise to break even" and "Hold at $X to clear" cannot both be the
  // suggested action. The verdict badge and the watchouts stay.
  const actionField = clearance ? (
    <div data-slot="clearance">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <p className="text-xs font-medium text-fg-secondary">Clearance</p>
      </div>
      <p className="text-sm font-medium text-fg-primary mt-1.5">{clearanceHeadline(clearance)}</p>
      <p className="text-xs text-fg-secondary mt-1.5">{clearance.reason}</p>
      {clearanceMoney && (
        <p className="text-xs text-fg-secondary mt-1.5 tabular-nums">{clearanceMoney}</p>
      )}
      {clearancePriceToTry !== null && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onSimulate(clearancePriceToTry)}
          className="mt-2 h-6 px-2 text-xs"
        >
          Use this price
        </Button>
      )}
      {sellerCentralBlock}
      {watchoutsBlock}
    </div>
  ) : (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <p className="text-sm font-medium text-fg-primary">{action.title}</p>
      </div>
      <p className="text-xs text-fg-secondary mt-1.5">{action.reason}</p>
      {sellerCentralBlock}
      {watchoutsBlock}
    </div>
  );

  const subRanks = row.salesRank?.subRanks ?? [];

  const expansion =
    expanded && mathCardInput ? (
      <div className="mt-3 overflow-x-auto">
        <MathCard data={buildMathCard(mathCardInput)} />
        <p className="mt-2 text-xs text-fg-tertiary tabular-nums">
          Sales history: 7d {row.unitsSold7d.toLocaleString('en-US')} · 30d{' '}
          {row.unitsSold30d.toLocaleString('en-US')} · 90d{' '}
          {row.unitsSold90d.toLocaleString('en-US')}
        </p>
        {subRanks.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {subRanks.map((subRank) => (
              <p key={`${subRank.title}-${subRank.rank}`} className="text-xs text-fg-tertiary">
                #{subRank.rank.toLocaleString('en-US')} in {subRank.title}
              </p>
            ))}
          </div>
        )}
        {competition && <CompetitorTable offers={competition.offers} />}
      </div>
    ) : null;

  return (
    <article
      data-row-layout="responsive"
      data-seller-sku={row.sellerSku}
      // Founder UAT finding 1 (2026-08-30): "hard to distinguish cells from
      // the page color" — the card had no fill at all (transparent over
      // bg-canvas), the same low-contrast idiom every other card list in
      // this app avoids (see DashboardPage/AmazonPage's `bg-surface border
      // border-border-subtle rounded-xl`). `isolate` + a negative-z overlay
      // (below) keeps the existing buy-box positive wash layered ON TOP of
      // this fill rather than replacing it outright.
      className={cn('relative isolate border border-border-subtle rounded-xl bg-surface px-4 py-3')}
    >
      {row.buyboxIsYours === true && (
        <div className="absolute inset-0 -z-10 rounded-xl bg-positive/[0.06]" aria-hidden="true" />
      )}
      {/* Desktop — four labeled jobs, one row per SKU. */}
      <div
        className="hidden xl:grid xl:grid-cols-[minmax(180px,1.25fr)_minmax(170px,0.95fr)_minmax(280px,1.7fr)_minmax(210px,1.1fr)] xl:gap-6 xl:items-start text-sm"
        data-row-variant="desktop"
      >
        <Field label="Product">{productField}</Field>
        <Field label="Inventory & demand">{unitsField}</Field>
        <Field label="Price scenario">{priceScenarioField}</Field>
        <Field label="Suggested action">{actionField}</Field>
      </div>

      {/* Tablet/mobile — stacked card, same fields, labeled sections. */}
      <div className="xl:hidden space-y-3 text-sm" data-row-variant="compact">
        <Field label="Product">{productField}</Field>
        <Field label="Inventory & demand">{unitsField}</Field>
        <Field label="Price scenario">{priceScenarioField}</Field>
        <Field label="Suggested action">{actionField}</Field>
      </div>

      <div className="mt-3 pt-3 border-t border-border-subtle flex items-center gap-x-3 gap-y-2 flex-wrap text-xs text-fg-tertiary">
        <span className="tabular-nums">{costLine(row)}</span>
        {row.feesAtCurrentPriceCents !== null && (
          <span title={feesSourceNote(row.feesSource) ?? undefined}>
            Fees {feesSourceTag(row.feesSource)}
            {row.feesCheckedAt && ` · checked ${priceFreshnessLabel(row.feesCheckedAt)}`}
          </span>
        )}
        <span>Price checked {priceFreshnessLabel(row.priceCheckedAt)}</span>
        <span className="inline-flex items-center gap-1">
          Purchasing <Badge variant={authBadge.variant}>{authBadge.label}</Badge>
        </span>
        {mathCardInput && (
          <button
            type="button"
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            className="ml-auto inline-flex items-center gap-1 text-accent-fg hover:underline"
          >
            How calculated
            {expanded ? (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      {expansion}
    </article>
  );
}

/** How long the Copy button says "Copied". */
const COPIED_RESET_MS = 1500;

/** The three numbers Seller Central asks for, in the Suggested action column.
 *  Copy puts `sku<TAB>price<TAB>min<TAB>max` on the clipboard. Same
 *  copy-then-confirm shape as the outreach drafts: best-effort, because the
 *  browser can refuse the clipboard. "now $X" shows the live price only when
 *  the price to set is a different number. */
function SellerCentralBlock({
  sku,
  prices,
  currentPriceCents,
}: {
  sku: string;
  prices: SellerCentralPrices;
  currentPriceCents: number | null;
}) {
  const [copied, setCopied] = useState(false);
  const resetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (resetRef.current) clearTimeout(resetRef.current);
    },
    []
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(sellerCentralCopyLine(sku, prices));
      setCopied(true);
      if (resetRef.current) clearTimeout(resetRef.current);
      resetRef.current = setTimeout(() => {
        setCopied(false);
        resetRef.current = null;
      }, COPIED_RESET_MS);
    } catch {
      // best-effort; clipboard may be denied in non-secure contexts
    }
  }

  const now =
    currentPriceCents !== null && currentPriceCents !== prices.priceCents
      ? currentPriceCents
      : null;

  return (
    <div
      data-slot="seller-central"
      role="group"
      aria-label="Seller Central prices"
      className="mt-3 pt-2.5 border-t border-border-subtle"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-fg-secondary">Seller Central</p>
        <Button variant="secondary" size="sm" onClick={copy} className="h-6 px-2 text-xs">
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-1.5 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-fg-tertiary">
        <SellerCentralFigure field="price" label="Price" cents={prices.priceCents} nowCents={now} />
        <SellerCentralFigure field="min" label="Min" cents={prices.minCents} />
        <SellerCentralFigure field="max" label="Max" cents={prices.maxCents} />
      </p>
      <p className="text-xs text-fg-tertiary mt-1.5">{sellerCentralBasisLine(prices)}</p>
    </div>
  );
}

/** One labeled figure: a small label and a monospace amount, or "n/a".
 *  `nowCents` adds a muted "now $X" after the figure (the Price figure only). */
function SellerCentralFigure({
  field,
  label,
  cents,
  nowCents = null,
}: {
  field: 'price' | 'min' | 'max';
  label: string;
  cents: number | null;
  nowCents?: number | null;
}) {
  return (
    <span>
      {label}{' '}
      <span
        data-field={field}
        className={cn('font-mono text-sm tabular-nums', cents !== null && 'text-fg-primary')}
      >
        {cents === null ? 'n/a' : money(cents)}
      </span>
      {nowCents !== null && (
        <span data-field="now" className="ml-1.5">
          {`now ${money(nowCents)}`}
        </span>
      )}
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-[0.08em] text-fg-tertiary mb-2">
        {label}
      </p>
      {children}
    </div>
  );
}

/** Empty-cell glyph for the Competing sellers table (an en dash, not the
 *  em dash money() prints for a missing amount). */
const NO_VALUE = '–';

/** Type column of the Competing sellers table: our own offer first, then
 *  Amazon, then the fulfilment channel. */
function offerTypeLabel(offer: CompetitorOffer): string {
  if (offer.isOurs) return 'You';
  if (offer.isAmazon) return 'Amazon';
  return offer.isFba ? 'FBA' : 'FBM';
}

/** Every live offer Keepa listed, in the order the API gave them. Our own
 *  offer is tinted, the Buy Box holder is badged, and Amazon's units are a
 *  dash because Keepa cannot read Amazon's stock. */
function CompetitorTable({ offers }: { offers: CompetitorOffer[] }) {
  return (
    <div className="mt-3">
      <p className="text-xs font-medium text-fg-secondary mb-1.5">Competing sellers</p>
      {offers.length === 0 ? (
        <p className="text-xs text-fg-tertiary">No offers were listed in the last check.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border-subtle">
          <table className="w-full min-w-[520px] text-xs text-left">
            <thead className="bg-surface-elev text-fg-secondary">
              <tr>
                <th className="px-3 py-1.5 font-medium">Seller</th>
                <th className="px-3 py-1.5 font-medium">Type</th>
                <th className="px-3 py-1.5 font-medium text-right">Price</th>
                <th className="px-3 py-1.5 font-medium text-right">Units</th>
                <th className="px-3 py-1.5 font-medium">Read</th>
                <th className="px-3 py-1.5 font-medium text-right">Buy Box 30 d</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {offers.map((offer, index) => (
                <tr key={index} className={cn(offer.isOurs && 'bg-accent/[0.08]')}>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center gap-2">
                      {offer.sellerName ?? 'Unknown seller'}
                      {offer.holdsBuyBox && <Badge variant="info">Buy Box</Badge>}
                    </span>
                  </td>
                  <td className={cn('px-3 py-1.5', offer.isOurs && 'font-medium')}>
                    {offerTypeLabel(offer)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {offer.landedCents === null ? NO_VALUE : money(offer.landedCents)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {offer.isAmazon || offer.stock === null
                      ? NO_VALUE
                      : offer.stock.toLocaleString('en-US')}
                  </td>
                  <td className="px-3 py-1.5 text-fg-tertiary">
                    {stockReadingLabel(offer.stockAgeMinutes)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {offer.sharePct30 === null ? NO_VALUE : `${Math.round(offer.sharePct30)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-1.5 text-xs text-fg-tertiary">
        Units are read by Keepa. Only the lowest-priced offers are re-read on each refresh; the Read
        column shows the age.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Price simulator stepper — moved verbatim from PriceCalibrationPage.tsx
// (Task C item 5's "play button"). Up/down buttons + an editable dollar
// field, step $0.25, arrow keys work. All arithmetic is integer cents via
// lib/price-calibration.ts's stepSimulatedPriceCents — the field itself
// only formats/parses dollars for display.
// ─────────────────────────────────────────────────────────

function PriceStepper({
  liveCents,
  simulatedCents,
  onSimulate,
}: {
  liveCents: number;
  simulatedCents: number | null;
  onSimulate: (cents: number | null) => void;
}) {
  const baseline = simulatedCents ?? liveCents;
  const [text, setText] = useState((baseline / 100).toFixed(2));
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setText((baseline / 100).toFixed(2));
  }, [baseline]);

  const isSimulating = simulatedCents !== null && simulatedCents !== liveCents;

  function step(direction: 1 | -1) {
    onSimulate(stepSimulatedPriceCents(baseline, direction));
  }

  function commit() {
    const cents = parseSimulatedPriceInput(text);
    if (cents === null) {
      setText((baseline / 100).toFixed(2));
      return;
    }
    onSimulate(cents === liveCents ? null : cents);
  }

  return (
    <div
      className={cn(
        'inline-flex items-center gap-0.5 rounded-sm',
        isSimulating && 'ring-1 ring-accent/60'
      )}
    >
      <button
        type="button"
        aria-label="Decrease simulated price"
        onClick={() => step(-1)}
        className="h-6 w-6 inline-flex items-center justify-center text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
      >
        <Minus className="h-3 w-3" aria-hidden="true" />
      </button>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        aria-label={`Simulate price, step $${(SIMULATOR_STEP_CENTS / 100).toFixed(2)}`}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          focusedRef.current = false;
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            step(1);
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            step(-1);
          }
        }}
        className="w-16 h-6 text-right text-xs tabular-nums bg-transparent border border-border-subtle rounded-sm px-1 focus-visible:outline-none focus-visible:border-accent"
      />
      <button
        type="button"
        aria-label="Increase simulated price"
        onClick={() => step(1)}
        className="h-6 w-6 inline-flex items-center justify-center text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
      </button>
    </div>
  );
}
